import { useContext, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Check, Loader2 } from "lucide-react";
import { PaymentContext } from "../../contexts/PaymentContext";
import { getCardBrandIcon } from "../../shared/Utils";
import { API_BASE } from "../../shared/Constants";
import CheckIcon from "../../assets/check.svg";
import { Button } from "../ui/button";
import MinimumAmountHint from "./MinimumAmountHint";

import { safeGetAccessToken } from "../../utils/auth";
import { reportApiError } from "../../utils/reportError";
import { chargeSavedCardErrorMessage } from "../../utils/paymentErrors";
import { formatAmountInput } from "../../utils/currency";
export default function SavedCardPayment() {
  const [singleValue, setSingleValue] = useState("");
  const [creditsToLoad, setCreditsToLoad] = useState("");
  const [minValue, setMinValue] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const {
    card,
    setPaymentStage,
    paymentOption,
    setPaymentToast,
    setAutoChargePaymentId,
    billingScope,
    activeOrganizationId,
  } = useContext(PaymentContext);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const buildBillingUrl = (path: string, params: Record<string, string>) => {
    const search = new URLSearchParams(params);
    if (billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    return `${API_BASE}/api/billing${path}?${search.toString()}`;
  };

  const formatValue = (inputValue: string) => {
    inputValue = inputValue.replace(/\D/g, "");
    if (inputValue.length > 8) inputValue = inputValue.slice(0, 8);
    while (inputValue.length < 3) inputValue = "0" + inputValue;
    const reals = inputValue.slice(0, -2);
    const cents = inputValue.slice(-2);
    return `${parseInt(reals, 10)}.${cents}`;
  };

  const handleInputChange = (e: any, setter: any) => {
    const inputValue = e.target.value;
    const formattedValue = formatValue(inputValue);
    if (parseFloat(formattedValue) > 30000) {
      setInputMessage("O valor máximo é R$ 30.000,00");
      setter("30000.00");
    } else {
      setInputMessage("");
      setter(formattedValue);
    }
  };

  const getCardExpiration = (expiration: string) => {
    const [month, year] = expiration.split("/");
    return month + "/" + year.slice(-2);
  };

  const handleSinglePayment = async () => {
    if (processing) return;
    if (singleValue === "" || parseFloat(singleValue) === 0) {
      setInputMessage("O valor não pode ser 0");
      return;
    } else if (parseFloat(singleValue) < 20) {
      setInputMessage("O valor mínimo é R$ 20,00");
      return;
    }
    if (billingScope === "organization" && !activeOrganizationId) {
      setPaymentToast({
        type: "error",
        message: "Selecione uma organização para realizar a recarga.",
      });
      return;
    }
    const url = buildBillingUrl("/charge-saved-card", {
      payment_id: card.payment_id,
      amount: singleValue,
    });
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    setProcessing(true);
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Auth ${access_token}`, "Content-Type": "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      if (data["status"] === "success") {
        setPaymentToast({ type: "success", message: "Pagamento efetuado com sucesso!" });
        setSuccess(true);
      } else if (data["status"] === "requires_action") {
        window.location.href = data["action_url"];
      }
    } else {
      reportApiError(response, { feature: "payment.savedCard.charge", method: "POST" });
      setPaymentToast({
        type: "error",
        message: chargeSavedCardErrorMessage(response.status),
      });
      setPaymentStage("payment");
    }
    setProcessing(false);
  };

  const handleAutomaticConfig = async () => {
    if (processing) return;
    if (minValue === "" || parseFloat(minValue) === 0) {
      setInputMessage("O valor mínimo não pode ser 0");
      return;
    }
    if (creditsToLoad === "" || parseFloat(creditsToLoad) < 20) {
      setInputMessage("O valor mínimo de recarga é R$ 20,00");
      return;
    } else if (parseFloat(creditsToLoad) > 10000) {
      setInputMessage("O valor máximo de recarga é R$ 10000,00");
      return;
    }
    if (billingScope === "organization" && !activeOrganizationId) {
      setPaymentToast({
        type: "error",
        message: "Selecione uma organização para configurar a recarga automática.",
      });
      return;
    }
    const url = buildBillingUrl("/config-auto-charge", {
      amount_to_load: creditsToLoad,
      threshold: minValue,
      payment_id: card.payment_id,
    });
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    setProcessing(true);
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Auth ${access_token}`, "Content-Type": "application/json" },
    });
    if (response.ok) {
      setSuccess(true);
      setAutoChargePaymentId(card.payment_id);
    } else {
      reportApiError(response, { feature: "payment.savedCard.config", method: "POST" });
      setPaymentToast({
        type: "error",
        message:
          "Não foi possível configurar o pagamento automático. Por favor, tente novamente mais tarde.",
      });
    }
    setProcessing(false);
  };

  const renderField = (
    label: string,
    value: string,
    setter: (v: string) => void,
    placeholder = "0,00",
    showMinimum = false
  ) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-semibold text-foreground">{label}</label>
      <div className="flex items-center overflow-hidden rounded-md border border-border transition-colors focus-within:border-slate-400">
        <span className="border-r border-border bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground">
          R$
        </span>
        <input
          type="text"
          value={formatAmountInput(value)}
          onChange={(e) => handleInputChange(e, setter)}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-background px-3 py-2.5 text-base font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
        />
      </div>
      {showMinimum && <MinimumAmountHint />}
    </div>
  );

  const renderCardDisplay = () => (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-semibold text-foreground">Método de pagamento</label>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3">
        <div className="flex items-center gap-3">
          <img
            src={getCardBrandIcon(card.card_brand)}
            alt="Card"
            className="h-8 w-8 rounded border border-border bg-muted p-0.5"
          />
          <div className="flex flex-col">
            <span className="font-mono text-[13px] font-semibold tracking-wider text-foreground">
              •••• •••• •••• {card.card_last4}
            </span>
            <span className="text-[11px] text-muted-foreground">
              Exp: {getCardExpiration(card.card_expiration)}
            </span>
          </div>
        </div>
        <span className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
          <Check className="h-3 w-3" />
          Verificado
        </span>
      </div>
    </div>
  );

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <img src={CheckIcon} alt="" className="h-16 w-16" />
        <h3 className="text-xl font-semibold tracking-tight text-success">
          {paymentOption === "single" ? "Pagamento realizado!" : "Recarga automática configurada!"}
        </h3>
        <p className="max-w-xs text-sm text-muted-foreground">
          {paymentOption === "single"
            ? "Seus créditos foram adicionados ao seu saldo."
            : "Sua conta será recarregada automaticamente quando necessário."}
        </p>
      </div>
    );
  }

  if (paymentOption === "single") {
    return (
      <div className="flex flex-col gap-5 p-6 text-left">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
            Pagamento com cartão
          </h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Escolha o valor a ser carregado em seu saldo.
          </p>
        </div>
        {renderField("Valor dos créditos", singleValue, setSingleValue, "0,00", true)}
        {inputMessage && <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>}
        {renderCardDisplay()}
        <Button onClick={handleSinglePayment} disabled={processing} className="w-full gap-2">
          {processing && <Loader2 className="h-4 w-4 animate-spin" />}
          {processing ? "Processando..." : "Confirmar pagamento"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-6 text-left">
      <div>
        <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
          Configurar recarga automática
        </h3>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Defina os valores para recarga automática quando seu saldo estiver baixo.
        </p>
      </div>
      {renderField("Valor da recarga", creditsToLoad, setCreditsToLoad, "0,00", true)}
      {renderField("Recarregar quando saldo for menor que", minValue, setMinValue)}
      {inputMessage && <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>}
      {renderCardDisplay()}
      <Button onClick={handleAutomaticConfig} disabled={processing} className="w-full gap-2">
        {processing && <Loader2 className="h-4 w-4 animate-spin" />}
        {processing ? "Configurando..." : "Configurar recarga"}
      </Button>
    </div>
  );
}
